<template>
  <div class="tw:flex tw:flex-1 tw:flex-col tw:overflow-hidden">
    <div
      v-if="!totalFilters && !query"
      class="tw:flex tw:flex-1 tw:items-center tw:justify-center tw:text-denim-600"
    >
      {{ getLocaleText("source_list.show_results") }}
    </div>
    <div
      v-else-if="!sources.length && !loading"
      class="tw:flex tw:flex-1 tw:items-center tw:justify-center tw:text-denim-600"
    >
      <div class="tw:flex tw:flex-col tw:items-center tw:gap-3">
        <div>
          <QIcon
            class="tw:text-danger"
            name="ion-information-circle"
            size="28px"
          />
        </div>
        <div class="tw:flex tw:flex-col tw:items-center tw:gap-1">
          <div>
            {{ getLocaleText("source_list.search_empty") }}
          </div>
          <div>
            {{ getLocaleText("source_list.expand_search") }}
          </div>
        </div>
        <template v-if="showEditStream">
          <div class="tw:flex tw:justify-center tw:gap-2">
            <div>{{ getLocaleText("source_list.edit_stream_heading") }}</div>
            <div>
              <a
                class="tw:cursor-pointer tw:text-blue-600 tw:hover:underline"
                @click="editStream"
              >
                {{ getLocaleText("source_list.edit_stream_label") }}
              </a>
            </div>
          </div>
        </template>
      </div>
    </div>
    <template v-else>
      <div class="tw:flex tw:justify-between tw:px-4 tw:py-3">
        <div>
          <Checkbox
            v-if="sources.length"
            data-testid="select-all"
            dense
            :indeterminate="selectAllIndetermediate"
            :label="getLocaleText('global.select_all')"
            :model-value="selectAll"
            new-design
            @update:model-value="updateSelectAll"
          />
        </div>
        <div>
          <span>{{ getLocaleText("source_list.sort_by_label") }}</span>

          <BaseButton
            data-testid="sort"
            dense
            :icon="orderByIcon"
            icon-right
            new-design
            new-icon
            @click="toggleOrderBy"
          >
            <span class="tw:font-bold">
              {{ getLocaleText("global.name") }}
            </span>
          </BaseButton>
        </div>
      </div>
      <LoadingBar
        v-if="loading"
        height="1"
      />
      <BaseSeparator v-else />
      <div
        class="tw:flex-1 tw:overflow-auto"
        data-testid="sources"
      >
        <template
          v-for="(source, sourceIndex) in sources"
          :key="`${source.type}_${source.id}`"
        >
          <BaseSeparator v-if="sourceIndex" />

          <SourceListItem
            :selected="isSourceSelected(source)"
            :source="source"
            @toggle="updateSelectedSource(source)"
          />
        </template>
        <template v-if="showEditStream">
          <BaseSeparator />
          <div class="tw:flex tw:justify-center tw:gap-2 tw:p-6">
            <div>{{ getLocaleText("source_list.edit_stream_heading") }}</div>
            <div>
              <a
                class="tw:cursor-pointer tw:text-blue-600 tw:hover:underline"
                @click="editStream"
              >
                {{ getLocaleText("source_list.edit_stream_label") }}
              </a>
            </div>
          </div>
        </template>
      </div>
      <BaseSeparator />
      <div class="tw:flex tw:justify-end tw:px-2 tw:py-1">
        <Pagination
          :max-pages="5"
          :page-size-options="[50, 100, 200]"
          :pagination="pagination"
          @pagination-changed="onPaginationChange"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { debounce } from "lodash-es";
import { computed, onMounted, ref, watch } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";
import { BaseButton, BaseSeparator } from "shared/components/base";
import LoadingBar from "shared/components/base/LoadingBar.vue";
import Checkbox from "shared/components/core/forms/Checkbox.vue";
import { type PaginationData, Pagination } from "shared/components/Pagination";
import useSourceFilters from "shared/composables/useSourceFilters";
import { streamTypes } from "shared/constants";
import type { Source } from "shared/helpers/sourceFilters";
import useSourcesStore from "shared/stores/sources";
import type { Nullable, Stream } from "shared/types";

import SourceListItem from "./SourceListItem.vue";

const {
  keywordFilters,
  mediaFilters,
  selectedCategories,
  topicFilters,
  selectedLocations,
  seletedSourceGroupIds,
  contentTypeFilters,
  totalFilters,
} = useSourceFilters();

const sourcesStore = useSourcesStore();

const selectedSources = defineModel<Source[]>("selectedSources", {
  default: () => [],
});

const totalSources = defineModel<number>("totalSources");

export interface SourceListProps {
  stream?: Nullable<Stream>;
  query?: string;
  editingStream?: boolean;
}

const props = withDefaults(defineProps<SourceListProps>(), {
  stream: null,
  query: "",
});

const emit = defineEmits(["edit-stream"]);

const pagination = ref<PaginationData>({
  page: 1,
  pageSize: 50,
  pageCount: 0,
});

const loading = ref(false);
const sources = ref<Source[]>([]);
const orderBy = ref<"asc" | "desc">("asc");

const selectedDifference = computed(() => {
  const sourceIds = sources.value.map((source) => source.id);
  const selectedSourceIds = selectedSources.value.map((source) => source.id);

  return sourceIds.filter((id) => !selectedSourceIds.includes(id));
});

const selectAll = computed(() => {
  if (!sources.value.length) return false;

  return !selectedDifference.value.length;
});

const selectAllIndetermediate = computed(() =>
  Boolean(
    selectedDifference.value.length &&
      selectedDifference.value.length !== sources.value.length
  )
);

const orderByIcon = computed<"sort-up" | "sort-down">(() =>
  orderBy.value === "asc" ? "sort-up" : "sort-down"
);

const lastPage = computed(
  () => pagination.value.page === pagination.value.pageCount
);

const showEditStream = computed(
  () =>
    !loading.value &&
    (lastPage.value || !sources.value.length) &&
    props.stream?.can_modify &&
    props.stream?.type === streamTypes.mentionStream &&
    !props.editingStream
);

async function loadSources() {
  if (!totalFilters.value && !props.query) {
    loading.value = false;
    totalSources.value = 0;
    sources.value = [];

    return;
  }

  try {
    const url = props.stream?.id
      ? `streams/${props.stream.id}/sources`
      : "sources";

    const result = await $streemApiV1.get(url, {
      params: {
        all_sources: props.editingStream,
        name: props.query,
        page: pagination.value.page,
        limit: pagination.value.pageSize,
        topic_ids: topicFilters.value,
        media: mediaFilters.value,
        category_ids: selectedCategories.value,
        source_group_ids: seletedSourceGroupIds.value,
        location_ids: selectedLocations.value,
        keywords: keywordFilters.value.map((filter) => filter.value),
        exclude_source_groups: true,
        order_by: orderBy.value,
        ...contentTypeFilters.value,
      },
    });

    const { data, headers } = result;
    pagination.value.pageCount = Number(headers["x-total-pages"]);
    totalSources.value = Number(headers["x-total-count"]);
    sources.value = data;
  } catch (error) {
    ErrorDialog(getLocaleText("source_list.load_error"), error);
  } finally {
    loading.value = false;
  }
}

const debounceLoadSource = debounce(loadSources, 500);

function startLoadSources() {
  loading.value = true;
  debounceLoadSource();
}

sourcesStore.$subscribe((mutation, { filters }) => {
  if (!filters) return;
  pagination.value.page = 1;
  startLoadSources();
});

function toggleOrderBy() {
  orderBy.value = orderBy.value === "asc" ? "desc" : "asc";
  startLoadSources();
}

function onPaginationChange({ page, pageSize }: PaginationData) {
  if (
    pagination.value.page === page &&
    pagination.value.pageSize === pageSize
  ) {
    return;
  }

  pagination.value.page = page;
  pagination.value.pageSize = pageSize;
  startLoadSources();
}

function isSourceSelected(source: Source) {
  return selectedSources.value.some(
    (selectedSource) =>
      selectedSource.id === source.id && selectedSource.type === source.type
  );
}

function updateSelectedSource(source: Source) {
  const selected = isSourceSelected(source);

  if (selected) {
    selectedSources.value = selectedSources.value.filter(
      (selectedSource) => selectedSource.id !== source.id
    );
  } else {
    selectedSources.value = selectedSources.value.concat([source]);
  }
}

function updateSelectAll() {
  if (selectAll.value) {
    const sourcesToRemove = sources.value
      .filter((source) => isSourceSelected(source))
      .map((source) => source.id);

    selectedSources.value = selectedSources.value.filter(
      (selectedSource) => !sourcesToRemove.includes(selectedSource.id)
    );

    return;
  }

  const sourcesToAdd = sources.value.filter(
    (source) => !isSourceSelected(source)
  );

  selectedSources.value = selectedSources.value.concat(sourcesToAdd);
}

function editStream() {
  emit("edit-stream");
}

watch(
  () => props.query,
  () => {
    pagination.value.page = 1;
    startLoadSources();
  }
);

onMounted(() => {
  startLoadSources();
});
</script>
